import { faStore } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from '@shared/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/shadcn/table';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { useClientShops } from '../../hooks';

interface ShopsTabProps {
  teamId: number;
}

export const ShopsTab = ({ teamId }: ShopsTabProps) => {
  const { t, i18n } = useTranslation('viewClientModule');
  const { shops, total, isLoading } = useClientShops(teamId);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const locale = i18n.language === 'es' ? es : undefined;
      return format(date, 'dd/MM/yyyy HH:mm', { locale });
    } catch {
      return '-';
    }
  };

  const isActive = (status: number) => status === 1;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (shops.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <FontAwesomeIcon icon={faStore} className="w-12 h-12 mb-4 opacity-50" />
        <p>{t('tabs.shops.empty')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t('tabs.shops.total', { count: total })}</p>
      </div>
      <div className="border border-card-border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('tabs.shops.channel')}</TableHead>
              <TableHead>{t('tabs.shops.name')}</TableHead>
              <TableHead>{t('tabs.shops.url')}</TableHead>
              <TableHead>{t('tabs.shops.lastSync')}</TableHead>
              <TableHead className="text-center">{t('tabs.shops.status')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shops.map((shop) => (
              <TableRow key={shop.id}>
                <TableCell>
                  {shop.image ? (
                    <img src={shop.image} alt="Channel" className="w-6 h-6 object-contain" />
                  ) : (
                    <FontAwesomeIcon icon={faStore} className="w-5 h-5 text-muted-foreground" />
                  )}
                </TableCell>
                <TableCell className="font-medium">{shop.name}</TableCell>
                <TableCell>
                  {shop.connect_data?.url ? (
                    <a
                      href={
                        shop.connect_data.url.startsWith('http')
                          ? shop.connect_data.url
                          : `https://${shop.connect_data.url}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline truncate block max-w-[250px]"
                    >
                      {shop.connect_data.url}
                    </a>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{formatDate(shop.last_synchronization)}</TableCell>
                <TableCell className="text-center">
                  <Badge variant={isActive(shop.status) ? 'default' : 'secondary'}>
                    {isActive(shop.status) ? t('tabs.shops.statusActive') : t('tabs.shops.statusInactive')}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
